// onboardingBuild.js — THE BUILD BOARD of a personalized sign-up (POB S6,
// PLAN §7b-2; decision 9 of 2026-10-25: the setup steps show IN the
// workspace). A person who signed up with their website read (POB) does not
// meet the welcome wizard: the workspace opens at once on ONE board that shows
// what sign-up really did — one card per server step, each with its live
// state, its result line and, when it made something, a way to it.
//
// WHO: the POB record — `user_preferences.onboarding = { v: 1, status:
// 'pending', via: 'pob', at }`, written by the SERVER at sign-up, write-once
// (the client never writes it; functions/onboarding.js onboardingEligible,
// reason 'pob-build'). The board is the server's: a workspace with no board
// (the sign-up was not POB — server core/pob-board-2 answers 200 with data
// `null`, an older server a 404 `not_found`; both read as "no board") shows
// nothing at all, and the record waits for a load that has one. THE SIGN-UP RACE (POB lead
// 2026-10-30): a Fly write can win the write-once record, so a PENDING FLY
// record whose workspace answers a board (200) is a personalized sign-up too
// — the gate's tick asks the board first on the home (`opts.orElse: 'ready'`
// below) and Fly's ready screen opens only when there is none.
//
// THE DATA IS THE SERVER'S, through the SDK only (feature-detected, never a
// version guess):
//   sdk.getOnboardingBuild(workspaceId)            (any member)
//     → { researchId, steps: [{ key, status: pending | running | done |
//         failed | skipped, label: { en, ka }, result?: { count }, link?,
//         error?: { code }, reason?: { code }, retryable?, at }], done,
//         checklist? }
//     (server core/pob-research 34c695c9c, OnboardingBuild's step schema).
//     The NESTED shape is the only one (POB lead 2026-10-30): a `count` or a
//     `code` flat on the step is not read. A board without a researchId,
//     `done` or a label still reads: done is "every step has an outcome", and
//     a card without a server label is named by the shell's own words.
//     `retryable` (server core/pob-board-2): true only for a failed `apps`
//     step with a preset; false hides Try again; no field (an older server)
//     keeps the client's rule. Kept as true | false | null.
//     `checklist: { profile, website, team, import }`, each `{ done, at }` —
//     the first thread's "Getting started" ticks (functions/
//     assistantFlyResearch.js); the ONE source of them when it is there. An
//     item the server left out is not done; no checklist (an older server)
//     is null.
//   sdk.retryOnboardingBuildStep(workspaceId, key) (manager; a failed step
//     only — v0: apps; 409 `not_retryable` otherwise)
// Step keys of v0: workspace, profile, apps, records, site. A skipped step
// always names its reason and the card says it in words — never a silent
// skip. The card's name is the server's own label, in the session language.
//
// THE POLL: every 2 s while a step is pending or running; it stops when the
// board is done, after 5 minutes (the board then says some steps still run,
// and the person may go on), on a workspace change, or when there is no
// board.
//
// THE WAY ON: "Open my workspace" shows once every step has an outcome (done,
// failed or skipped) — or the 5 minutes passed. Closing the board decides
// `done, via 'pob'` only then (functions/onboarding.js
// onboardingCloseDecision); an early close (Escape) decides nothing, so the
// board comes back on the next load.
//
//   root._pobBuild = { ws, status: 'ok' | 'none' | 'error', at, board, code,
//                      since, timedOut, retrying, note, canRetry }
// `canRetry` — the SDK in this page has `retryOnboardingBuildStep`; without it
// a failed card shows no Try again (no dead button).
//
// FRANK: every export is self-contained (FA204) — the vocabularies and the
// windows are restated where they are used; siblings are reached through
// `this.call`.

// THE READ — one in flight per workspace; an answer for a workspace that is no
// longer active drops itself. A 200 with no data (`null`, or `{ success:
// true, data: null }` — server core/pob-board-2) and a 403 / 404 /
// `not_found` (an older server) are "no board here" (`none`), never an error
// on screen; a failed read keeps the lines already on screen. `opts.sdk` — a
// test seam. → a promise of the stored entry, or null when nothing was asked
// (no workspace, no SDK read).
export function onboardingBuildRead(rootIn, opts) {
  const STATUSES = ['pending', 'running', 'done', 'failed', 'skipped']
  const o = opts && typeof opts === 'object' ? opts : {}
  const el = this
  const root = rootIn || (el && el.state && el.state.root) || null
  if (!root || typeof root !== 'object' || typeof root.update !== 'function')
    return Promise.resolve(null)
  const wsOf = (x) => {
    const w = x && x.workspace
    return String((w && (w.id || w._id)) || '')
  }
  const wsId = wsOf(root)
  if (!wsId) return Promise.resolve(null)
  const fns = (el && el.context && el.context.functions) || {}
  let sdk = o.sdk || null
  if (!sdk && typeof fns.getSDK === 'function') {
    try {
      sdk = el.call('getSDK')
    } catch (_) {
      sdk = null
    }
  }
  // The feature gate is the method itself (the shell's init-await proxy reads
  // a method the SDK lacks as undefined).
  if (!sdk || sdk.isNullClient === true || typeof sdk.getOnboardingBuild !== 'function') {
    return Promise.resolve(null)
  }
  const flight = root.__pobBuildFlight
  if (flight && flight.ws === wsId && flight.promise) return flight.promise
  const prevOf = () =>
    root._pobBuild && typeof root._pobBuild === 'object' && root._pobBuild.ws === wsId
      ? root._pobBuild
      : null
  // the nested shape only: `result.count`, `error.code`, `reason.code`
  const count = (x) => {
    const res = x.result && typeof x.result === 'object' ? x.result : null
    const n = res && res.count != null ? Number(res.count) : NaN
    return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null
  }
  const codeOf = (x) => (x && typeof x === 'object' && typeof x.code === 'string' ? x.code : '')
  // the first thread's four items; one the server left out is not done
  const ITEMS = ['profile', 'website', 'team', 'import']
  const checklist = (c) => {
    if (!c || typeof c !== 'object' || Array.isArray(c)) return null
    const out = {}
    for (const k of ITEMS) {
      const x = c[k] && typeof c[k] === 'object' ? c[k] : null
      out[k] = {
        done: !!(x && x.done === true),
        at: x && x.done === true && typeof x.at === 'string' ? x.at : null
      }
    }
    return out
  }
  const clean = (board) => ({
    researchId: board.researchId ? String(board.researchId) : '',
    done: board.done === true,
    checklist: checklist(board.checklist),
    steps: board.steps
      .filter((x) => x && typeof x.key === 'string' && x.key)
      .map((x) => ({
        key: x.key,
        status: STATUSES.indexOf(x.status) !== -1 ? x.status : 'pending',
        label: {
          en: String((x.label && x.label.en) || ''),
          ka: String((x.label && x.label.ka) || '')
        },
        count: count(x),
        link: typeof x.link === 'string' ? x.link : '',
        error: codeOf(x.error),
        reason: codeOf(x.reason),
        retryable: typeof x.retryable === 'boolean' ? x.retryable : null,
        at: x.at || null
      }))
  })
  const none = (status, code) =>
    status === '403' || status === '404' || code === 'not_found' || code === 'forbidden'
  const promise = Promise.resolve()
    .then(() => sdk.getOnboardingBuild(wsId))
    .then(
      (res) => {
        const bare = res && typeof res === 'object' && !Array.isArray(res) ? res : null
        if (bare && bare.success === false) {
          const code = String(bare.error || bare.code || 'failed')
          return none(String(bare.status || ''), code)
            ? { status: 'none', board: null, code }
            : { status: 'error', board: null, code }
        }
        const board =
          bare && 'success' in bare && 'data' in bare
            ? bare.data && typeof bare.data === 'object'
              ? bare.data
              : null
            : bare
        if (!board) return { status: 'none', board: null, code: '' }
        if (!Array.isArray(board.steps)) return { status: 'error', board: null, code: 'shape' }
        return { status: 'ok', board: clean(board), code: '' }
      },
      (err) => {
        const status = String((err && (err.status || err.statusCode)) || '')
        const code = String((err && (err.code || err.error)) || status || 'failed')
        return none(status, code)
          ? { status: 'none', board: null, code }
          : { status: 'error', board: null, code }
      }
    )
    .then((got) => {
      if (root.__pobBuildFlight && root.__pobBuildFlight.promise === promise)
        root.__pobBuildFlight = null
      if (wsOf(root) !== wsId) return null
      const prev = prevOf()
      const next = {
        ws: wsId,
        status: got.status,
        at: Date.now(),
        board: got.board,
        code: got.code,
        since: (prev && prev.since) || Date.now(),
        timedOut: !!(prev && prev.timedOut),
        retrying: (prev && prev.retrying) || '',
        note: (prev && prev.note) || '',
        canRetry: typeof sdk.retryOnboardingBuildStep === 'function'
      }
      // An earlier answer stays on screen through a failed poll (a network
      // blip must not wipe the cards the person is reading).
      if (got.status === 'error' && prev && prev.status === 'ok' && prev.board) {
        next.status = 'ok'
        next.board = prev.board
      }
      root.update({ _pobBuild: next }, { preventFetch: true })
      return next
    })
  root.__pobBuildFlight = { ws: wsId, promise }
  return promise
}

// THE POLL — one loop per workspace and page (`root.__pobBuildPoll`). It asks
// at once, then every 2 s while the board has a step pending or running; it
// ends when the board is done (or every step has an outcome), when there is
// no board, on a workspace change, or after 5 minutes — then the entry says
// `timedOut` and the board lets the person go on. `opts.force` starts a fresh
// loop (after a retry: a fresh 5 minutes). Test seams: `opts.sdk`,
// `opts.everyMs`, `opts.maxMs`. → true when a loop runs.
export function onboardingBuildPoll(rootIn, opts) {
  const EVERY_MS = 2000
  const MAX_MS = 5 * 60 * 1000
  const OUTCOMES = ['done', 'failed', 'skipped']
  const o = opts && typeof opts === 'object' ? opts : {}
  const el = this
  const root = rootIn || (el && el.state && el.state.root) || null
  if (!root || typeof root !== 'object' || !el || typeof el.call !== 'function') return false
  const wsOf = (x) => {
    const w = x && x.workspace
    return String((w && (w.id || w._id)) || '')
  }
  const wsId = wsOf(root)
  if (!wsId) return false
  const every = typeof o.everyMs === 'number' && o.everyMs >= 0 ? o.everyMs : EVERY_MS
  const max = typeof o.maxMs === 'number' && o.maxMs >= 0 ? o.maxMs : MAX_MS
  const cur = root.__pobBuildPoll
  if (cur && cur.ws === wsId && cur.live && !o.force) return true
  if (cur && cur.timer) clearTimeout(cur.timer)
  const poll = { ws: wsId, live: true, since: Date.now(), timer: null, tick: null }
  root.__pobBuildPoll = poll
  const end = () => {
    poll.live = false
    if (poll.timer) clearTimeout(poll.timer)
    poll.timer = null
  }
  const settled = (board) =>
    !!board &&
    (board.done === true ||
      (board.steps.length > 0 && board.steps.every((x) => OUTCOMES.indexOf(x.status) !== -1)))
  // a fresh loop starts a fresh window: the board's own mark goes with it
  const entry = root._pobBuild
  if (o.force && entry && entry.ws === wsId && entry.timedOut) {
    root.update({ _pobBuild: { ...entry, timedOut: false } }, { preventFetch: true })
  }
  poll.tick = () => {
    poll.timer = null
    if (!poll.live || root.__pobBuildPoll !== poll) return
    if (wsOf(root) !== wsId) return end()
    if (Date.now() - poll.since >= max) {
      end()
      const now = root._pobBuild
      if (now && now.ws === wsId && !now.timedOut) {
        root.update({ _pobBuild: { ...now, timedOut: true } }, { preventFetch: true })
      }
      return
    }
    Promise.resolve(el.call('onboardingBuildRead', root, { sdk: o.sdk || null }))
      .catch(() => null)
      .then((got) => {
        if (!poll.live || root.__pobBuildPoll !== poll) return
        if (!got || got.status === 'none') return end()
        if (got.status === 'ok' && settled(got.board)) return end()
        poll.timer = setTimeout(poll.tick, every)
      })
  }
  poll.tick()
  return true
}

// OPEN — the gate's tick for the POB record (functions/onboarding.js
// onboardingTick). The board is read first, for at most `opts.waitMs`
// (4000 ms): a board → the setup surface opens on its 'build' step and the
// poll runs; no board (200 + null, or 404), no SDK read, a failed or slow read → nothing
// opens, the hold lets go and the record stays pending (no decision without a
// board the person saw). `opts.orElse: 'ready'` — the sign-up race (a pending
// Fly record on the home): with no board, Fly's ready screen opens instead
// (functions/onboardingReady.js), the hold handed to it as before.
// → a promise of true when the board opened.
export function onboardingBuildOpen(opts) {
  const el = this
  const root = el && el.state && el.state.root
  if (!root || typeof root.update !== 'function') return Promise.resolve(false)
  const o = opts && typeof opts === 'object' ? opts : {}
  const waitMs = typeof o.waitMs === 'number' && o.waitMs >= 0 ? o.waitMs : 4000
  const letGo = () => {
    if (root._onboardingHold && !root._onboardingFlow) {
      root.update({ _onboardingHold: false }, { preventFetch: true })
    }
    return false
  }
  // no board: the race's way out is Fly's ready screen (it takes the surface
  // and the hold, as the tick's own call did); else the hold lets go
  const noBoard = () => {
    if (o.orElse === 'ready' && !root._onboardingFlow) {
      el.call('onboardingReadyOpen')
      return false
    }
    return letGo()
  }
  let timer = null
  const cap = new Promise((resolve) => {
    timer = setTimeout(() => resolve(null), waitMs)
  })
  let read
  try {
    read = Promise.resolve(el.call('onboardingBuildRead', root, { sdk: o.sdk || null })).catch(
      () => null
    )
  } catch (_) {
    read = Promise.resolve(null)
  }
  return Promise.race([read, cap])
    .then((got) => {
      if (timer) clearTimeout(timer)
      if (!got || got.status !== 'ok' || !got.board) return noBoard()
      if (root._onboardingFlow || root._onboardingDecision) return letGo()
      const opened = el.call('openOnboarding', 'build')
      if (!opened) return letGo()
      el.call('onboardingBuildPoll', root, { sdk: o.sdk || null })
      return true
    })
    .catch((err) => {
      console.warn('[onboarding] the build board did not open:', err && err.message)
      return noBoard()
    })
}

// TRY AGAIN — a failed step once more (`retryOnboardingBuildStep`, owner /
// admin only — the button shows only for them), then a fresh poll. A refusal
// (409 `not_retryable`) or a failure leaves one plain note under the board.
// `opts`: { root, sdk } — test seams. → a promise of true when the server
// took it.
export function onboardingBuildRetry(key, opts) {
  const o = opts && typeof opts === 'object' ? opts : {}
  const el = this
  const root = (o.root && typeof o.root === 'object' && o.root) || (el && el.state && el.state.root)
  if (!root || typeof root.update !== 'function') return Promise.resolve(false)
  const w = root.workspace
  const wsId = String((w && (w.id || w._id)) || '')
  const fns = (el && el.context && el.context.functions) || {}
  const entry = root._pobBuild
  if (!wsId || !entry || entry.ws !== wsId || entry.status !== 'ok' || !entry.board) {
    return Promise.resolve(false)
  }
  if (entry.retrying) return Promise.resolve(false)
  const step = entry.board.steps.find((x) => x.key === key)
  // a failed step only, and never one the server marked `retryable: false`
  if (!step || step.status !== 'failed' || step.retryable === false) return Promise.resolve(false)
  if (typeof fns.currentUserIsAdmin !== 'function' || !el.call('currentUserIsAdmin', root)) {
    return Promise.resolve(false)
  }
  let sdk = o.sdk || null
  if (!sdk && typeof fns.getSDK === 'function') {
    try {
      sdk = el.call('getSDK')
    } catch (_) {
      sdk = null
    }
  }
  if (!sdk || typeof sdk.retryOnboardingBuildStep !== 'function') return Promise.resolve(false)
  root.update({ _pobBuild: { ...entry, retrying: key, note: '' } }, { preventFetch: true })
  const settle = (ok, note) => {
    const cur = root._pobBuild
    if (cur && cur.ws === wsId) {
      root.update({ _pobBuild: { ...cur, retrying: '', note: note || '' } }, { preventFetch: true })
    }
    if (ok) el.call('onboardingBuildPoll', root, { force: true, sdk: o.sdk || null })
    return ok
  }
  return Promise.resolve()
    .then(() => sdk.retryOnboardingBuildStep(wsId, key))
    .then(
      (res) => {
        const bare = res && typeof res === 'object' ? res : null
        if (bare && bare.success === false) {
          const code = String(bare.error || bare.code || '')
          return settle(false, code === 'not_retryable' ? 'refused' : 'failed')
        }
        return settle(true, '')
      },
      (err) => {
        const status = String((err && (err.status || err.statusCode)) || '')
        const code = String((err && (err.code || err.error)) || '')
        console.warn('[onboarding] build retry refused:', status || code)
        return settle(false, status === '409' || code === 'not_retryable' ? 'refused' : 'failed')
      }
    )
}

// A card's way to what its step made: the board closes (a settled board
// decides `done, via 'pob'`), then the place opens in the page slot, after the
// surface's exit. Through the app root: the card leaves with the board.
export function onboardingBuildGo(href) {
  const el = this
  const to = typeof href === 'string' ? href : ''
  if (!el || !to || to.charAt(0) !== '/') return false
  el.call('closeOnboarding', 'close')
  const host = (typeof el.getRoot === 'function' && el.getRoot()) || el
  setTimeout(() => host.call('openInSlot', to, host), 320)
  return true
}

// THE VIEW — what the board shows, from the entry and the session language.
// Pure over `root`.
//   { show, settled, ctaShown, timedOut, rows, noteText, footText }
// A row carries EVERY key a card reads (a childrenAs-'state' child falls back
// to the root's keys for a missing one), its `key` is positional (never a
// word that could be a tag), and every flag is a strict boolean.
export function onboardingBuildView(rootIn) {
  const OUTCOMES = ['done', 'failed', 'skipped']
  // the card's name when the server sent none (the v0 steps)
  const NAMES = {
    workspace: 'workspace.pobBuild.stepWorkspace',
    profile: 'workspace.pobBuild.stepProfile',
    apps: 'workspace.pobBuild.stepApps',
    records: 'workspace.pobBuild.stepRecords',
    site: 'workspace.pobBuild.stepSite'
  }
  // a skipped step's reason, in words (a code the client does not know reads
  // as the plain "Skipped")
  const REASONS = {
    offered_as_import: 'workspace.pobBuild.offered',
    no_offerings_found: 'workspace.pobBuild.noOfferings',
    site_offer_pending: 'workspace.pobBuild.sitePending',
    no_site_found: 'workspace.pobBuild.noSite',
    no_preset: 'workspace.pobBuild.noPreset',
    research_deleted: 'workspace.pobBuild.researchDeleted',
    research_expired: 'workspace.pobBuild.researchExpired',
    research_not_done: 'workspace.pobBuild.researchNotDone',
    research_invalid: 'workspace.pobBuild.noResearch',
    no_research: 'workspace.pobBuild.noResearch'
  }
  const ICONS = {
    done: 'checkmark',
    failed: 'warning',
    skipped: 'minus',
    pending: 'clock',
    running: 'clock'
  }
  const HIDDEN = {
    show: false,
    settled: false,
    ctaShown: false,
    timedOut: false,
    rows: [],
    noteText: '',
    footText: ''
  }
  const el = this
  const root = rootIn || (el && el.state && el.state.root) || null
  if (!root || typeof root !== 'object') return HIDDEN
  const w = root.workspace
  const wsId = String((w && (w.id || w._id)) || '')
  const entry = root._pobBuild && typeof root._pobBuild === 'object' ? root._pobBuild : null
  if (!wsId || !entry || entry.ws !== wsId || entry.status !== 'ok' || !entry.board) return HIDDEN
  const word = (key, params) => {
    if (!key) return ''
    const raw = el.call('polyglot', key)
    let out = raw == null ? '' : String(raw)
    const p = params && typeof params === 'object' ? params : {}
    for (const token of Object.keys(p)) out = out.split(token).join(String(p[token]))
    return out
  }
  const lang = String(el.call('getActiveLang') || root.lang || 'en')
  const fns = (el.context && el.context.functions) || {}
  const admin =
    typeof fns.currentUserIsAdmin === 'function' && !!el.call('currentUserIsAdmin', root)
  // what a done step made, in words
  const doneText = (x) => {
    const n = x.count
    if (x.key === 'workspace') return word('workspace.pobBuild.workspaceDone')
    if (x.key === 'profile' && n) {
      return n === 1
        ? word('workspace.pobBuild.profileDoneOne')
        : word('workspace.pobBuild.profileDone', { '{count}': n })
    }
    if (x.key === 'apps' && n != null) {
      return n === 1
        ? word('workspace.pobBuild.appsDoneOne')
        : word('workspace.pobBuild.appsDone', { '{count}': n })
    }
    if (x.key === 'records' && n) {
      return n === 1
        ? word('workspace.pobBuild.recordsDoneOne')
        : word('workspace.pobBuild.recordsDone', { '{count}': n })
    }
    if (x.key === 'site') return word('workspace.pobBuild.siteDone')
    return word('workspace.pobBuild.done')
  }
  const skipText = (x) => {
    if (x.reason === 'offered_as_import' && x.count) {
      return x.count === 1
        ? word('workspace.pobBuild.offeredOne')
        : word('workspace.pobBuild.offered', { '{count}': x.count })
    }
    if (x.reason === 'offered_as_import') return word('workspace.pobBuild.offeredNone')
    return word(REASONS[x.reason] || 'workspace.pobBuild.skipped')
  }
  const rows = entry.board.steps.map((x, i) => {
    const done = x.status === 'done'
    const failed = x.status === 'failed'
    const skipped = x.status === 'skipped'
    const running = x.status === 'running'
    const pending = x.status === 'pending'
    const label = (x.label && (x.label[lang] || x.label.en)) || ''
    // a way to what the step made: the server's own link (a path in this
    // workspace), else the apps it installed
    const href = done
      ? x.link && x.link.charAt(0) === '/'
        ? x.link
        : x.key === 'apps' && x.count
          ? '/apps'
          : ''
      : ''
    return {
      key: 'pob-build-' + (i + 1),
      step: x.key,
      status: x.status,
      title: label || word(NAMES[x.key] || '') || x.key,
      icon: ICONS[x.status] || 'clock',
      done,
      failed,
      skipped,
      running: running || pending,
      resultText: done
        ? doneText(x)
        : failed
          ? word('workspace.pobBuild.notDone')
          : skipped
            ? skipText(x)
            : running
              ? word('workspace.pobBuild.running')
              : word('workspace.pobBuild.pending'),
      // a done step that made only part of it (some apps did not install)
      partText: done && x.error === 'some_failed' ? word('workspace.pobBuild.appsSomeFailed') : '',
      // the server's word first (`retryable: false` — not this step); no word
      // (an older server): every failed step, for an owner or admin
      retryable: failed && admin && entry.canRetry !== false && x.retryable !== false,
      retrying: entry.retrying === x.key,
      linkHref: href,
      linkText: href
        ? x.key === 'apps'
          ? word('workspace.pobBuild.seeApps')
          : word('workspace.pobBuild.see')
        : ''
    }
  })
  const settled =
    entry.board.done === true ||
    (rows.length > 0 && entry.board.steps.every((x) => OUTCOMES.indexOf(x.status) !== -1))
  const timedOut = entry.timedOut === true && !settled
  const noteText =
    entry.note === 'refused'
      ? word('workspace.pobBuild.retryRefused')
      : entry.note === 'failed'
        ? word('workspace.pobBuild.retryFailed')
        : timedOut
          ? word('workspace.pobBuild.timedOut')
          : ''
  return {
    show: true,
    settled,
    ctaShown: settled || timedOut,
    timedOut,
    rows,
    noteText,
    footText: settled ? word('workspace.pobBuild.ready') : ''
  }
}
